import { useState } from "react";
import {
  GetAllOrdersForStaffDocument,
  OrderStatus,
} from "../../../generated/graphql";
import { useOrderListFilter } from "../contexts/OrderListFilterContext";
import { useLazyQuery } from "@apollo/client";
import { LIMIT_PAGINATION_QUANTITY } from "../../../constants/orders";
import { notifications } from "@mantine/notifications";
import { toApiSearch } from "../utils/orderSearch";

export interface SelectableOrder {
  id: string;
  status: OrderStatus;
  providerGroupId?: string | null;
}

export const MAX_SELECTED_ORDERS = 1000;

export const useOrderSelection = () => {
  const [selectedOrders, setSelectedOrders] = useState<SelectableOrder[]>([]);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);

  const { getFilterQuery, search } = useOrderListFilter();

  const [fetchAllOrders] = useLazyQuery(GetAllOrdersForStaffDocument, {
    fetchPolicy: "network-only",
  });

  const toggleOrder = (order: SelectableOrder) => {
    const foundOrderIndex = selectedOrders.findIndex(
      selectedOrder => selectedOrder.id === order.id
    );

    if (foundOrderIndex >= 0) {
      setSelectedOrders([
        ...selectedOrders.slice(0, foundOrderIndex),
        ...selectedOrders.slice(foundOrderIndex + 1),
      ]);
      return;
    }

    setSelectedOrders([...selectedOrders, order]);
  };

  const toggleSelectPage = (page: number, toggledOrders: SelectableOrder[]) => {
    if (selectedPages.includes(page)) {
      const toggledOrderIds = new Set(toggledOrders.map(({ id }) => id));
      setSelectedOrders(
        selectedOrders.filter(order => !toggledOrderIds.has(order.id))
      );

      setSelectedPages(selectedPages.filter(p => p !== page));
      return;
    }

    const currentlySelectedOrderIds = new Set(
      selectedOrders.map(({ id }) => id)
    );
    const ordersToAdd = toggledOrders.filter(
      ({ id }) => !currentlySelectedOrderIds.has(id)
    );
    setSelectedOrders([...selectedOrders, ...ordersToAdd]);
    setSelectedPages([...selectedPages, page]);
  };

  const unselectAll = () => {
    setSelectedOrders([]);
    setSelectedPages([]);
  };

  const selectAllPages = async () => {
    const result = await fetchAllOrders({
      variables: {
        take: MAX_SELECTED_ORDERS,
        where: getFilterQuery(),
        search: toApiSearch(search),
      },
    });

    const orders = result.data?.getAllOrdersForStaff?.orders ?? [];
    const totalCount = result.data?.getAllOrdersForStaff?.totalCount ?? 0;
    if (!result.data?.getAllOrdersForStaff) {
      notifications.show({
        title: "Unable to load orders",
        color: "red",
        message: "",
      });
      return;
    }

    const selectedCount = orders.length;
    const pagesNumber = Math.ceil(selectedCount / LIMIT_PAGINATION_QUANTITY);

    setSelectedOrders(orders);
    setSelectedPages(Array.from(Array(pagesNumber).keys()));

    const notification =
      totalCount === selectedCount
        ? {
            title: `Selected all ${totalCount} orders`,
            color: "blue",
            message: "All orders matching current filters have been selected",
          }
        : {
            title: `Selected ${selectedCount} of ${totalCount} orders`,
            color: "orange",
            message: `Only the first ${MAX_SELECTED_ORDERS} orders (${pagesNumber} pages) have been selected.`,
          };

    notifications.show(notification);
  };

  return {
    toggleOrder,
    toggleSelectPage,
    unselectAll,
    selectAllPages,
    selectedOrders,
    selectedPages,
  };
};
